import React from 'react';

import _ from 'lodash';
import { useSelector } from 'react-redux';

import { WidgetsContext } from './context.jsx';

import { FORM, FORM_STEP, FORM_FIELD, PAGE, EXTERNAL_FORM, LIVE_ELEMENT_CONTAINER, FORM_NUMBER, FORM_TEXTAREA, FORM_DROPDOWN_LIST, FORM_CHECK_LIST, FORM_CHECK_BOX, HOTSPOT, OTP_INPUT } from '../../../constants/widgetTypes';

export function useWidgetSelector(id) {
  const context = React.useContext(WidgetsContext);
  const selector = context.selector;

  var widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);
  return widgets.find(item => {
    return item.uid === id;
  });
}

export function useWidgetChildrenSelector(parent) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  return widgets.filter(item => {
    return item.parent === parent;
  }).filter(item => _.get(item, `properties.${context.breakpoint}.visible`, true))
    .sort((w1, w2) => {
      const o1 = _.get(w1, 'order') || 1;
      const o2 = _.get(w2, 'order') || 1;

      return o1 - o2;
    });
}

export function useWidgetSelectorForType(type) {
  const context = React.useContext(WidgetsContext);
  const selector = context.selector;

  var widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);
  return widgets.filter(item => {
    return item.type === type && _.get(item, `properties.${context.breakpoint}.visible`);
  });
}

export function getHotspotWidget(id) {
  const widgetContext = React.useContext(WidgetsContext);
  const selector = widgetContext.selector;
  var widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);
  var hotspotWidget = widgets.find(item => item.parent === id && item.type === HOTSPOT && _.get(item, `properties.${widgetContext?.breakpoint}.visible`));
  if (hotspotWidget){
    return true
  }
  return false
}

/**
 * Return Closest parent for type
*/
export function useParentWidgetSelectorForType(widget, widgetType) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const _findParent = (widgets, widget) => {
    const item = findWidget(widgets, widget);

    if (!item) {
      return null;
    }

    const type = _.get(item, 'type');
    if (type === widgetType) {
      return item;
    }

    if (item && item.parent) {
      return _findParent(widgets, item.parent)
    }
  }

  return _findParent(widgets, widget);
}

/**
 * Return closest container for widget
*/
export function useContainerSelector(widget) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);
  const item = widgets.find(item => item.uid === widget);

  if (item && item.parent) {
    return widgets.find(element => element.uid === item.parent);
  }

  return null;
}


/**
 * Return Form for Form field
*/
export function useForm(parent) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const form = widgets.find(item => {
    return item.type === FORM && item.uid === parent
  });

  return form;
}


/**
 * Return Input Widgets for Form
*/
export function useFormInputs(form) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  var formSteps = widgets.filter(item => item.type === FORM_STEP && item.parent === form);

  formSteps = _.orderBy(formSteps, ['order'], ['asc']);
  const formStep = formSteps[0];

  const formFieldTypes = [ FORM_FIELD, FORM_NUMBER, FORM_TEXTAREA, FORM_DROPDOWN_LIST, FORM_CHECK_LIST, FORM_CHECK_BOX ];

  let inputWidgets = widgets.filter(item => {
    return _.includes(formFieldTypes, item.type) && item.parent === formStep.uid
  });
  
  return inputWidgets;
}

export function useOtpFormInput(form) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  var formOtpStep = widgets.filter(item => item.type === FORM_STEP && item.parent === form && item.name == "OTP");


  const formFieldTypes = [ OTP_INPUT ];

  let otpInputWidget = widgets.filter(item => {
    return _.includes(formFieldTypes, item.type) && (item.parent === formOtpStep[0]?.uid)
  });
  return otpInputWidget;
}


/**
 * Return closest page widget for widget
*/
export function usePageWidgetSelector(widget) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const _findParent = (widgets, widget) => {
    const item = findWidget(widgets, widget);

    if (!item) {
      return null;
    }

    const type = _.get(item, 'type');
    if (type === PAGE) {
      return item;
    }

    if (item && item.parent) {
      return _findParent(widgets, item.parent)
    }
  }

  return _findParent(widgets, widget);
}

function findWidget(widgets, widget) {
  return widgets.find(item => item.uid === widget);
}

/**
 * Return if widgets parent is liveElemenet
*/
export function useLiveElementSelector(widget) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const _findParent = (widgets, widget) => {
    const item = findWidget(widgets, widget);

    if (!item) {
      return false;
    }

    const type = _.get(item, 'type');
    if (type === LIVE_ELEMENT_CONTAINER) {
      return true;
    }

    if (item && item.parent) {
      return _findParent(widgets, item.parent)
    }
  }

  return _findParent(widgets, widget);
}

/**
 * Return if any form has success state
*/
export function useFormSuccessSelector(selector) {
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const formWidgets = widgets.filter(item => item.type === FORM || item.type === EXTERNAL_FORM);

  var formSuccess = formWidgets.some(item => item.success);
  return formSuccess;
}

/**
 * Return if any form has otp-success state
*/
export function useOtpFormSuccessSelector(selector) {
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const formWidgets = widgets.filter(item => item.type === FORM);

  var formOtpSuccess = formWidgets.some(item => item.otpSuccess);
  return formOtpSuccess;
}

/**
 * Return if any form has otp required state
*/
export function useOtpRequiredSelector(selector) {
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const formWidgets = widgets.filter(item => item.type === FORM);

  var otpRequired = formWidgets.some(item => _.get(item, `properties.desktop.emailOtp`, false));
  return otpRequired;
}

/**
 * Return sum of the totol time spent in page
*/
export function usePageDuration(alias, storyboardType, isSmartGating = false) {
  const analytics = useSelector(state => _.get(state, 'pages.analytics', {}));
  const pages = useSelector(state => _.get(state, `pages.analytics.${alias}.pages`, []));
  const spatialPages = useSelector(state => _.get(state, 'pages.spatial.items', []));

  const spatialPageCount = spatialPages.length;

  if (storyboardType === 'SPATIAL') {
    if (isSmartGating) {
      const total = spatialPages.reduce(
        (sum, page) => sum + (_.get(analytics[page.canonical], 'duration', 0)),
        0
      );
      return spatialPageCount > 0 ? total / spatialPageCount : 0;
    } else {
      return _.get(analytics, `${alias}.duration`, 0);
    }
  }
  let duration = _.reduce(pages, (sum, page) => sum + page.duration, 0)

  return duration;
}

/**
 * Return sum of the total percentage spent in page
*/

export function usePagePercentage(alias, storyboardType, isSmartGating = false) {
  const analytics = useSelector(state => _.get(state, 'pages.analytics', {}));
  const pages = useSelector(state => _.get(state, `pages.analytics.${alias}.pages`, []));
  const spatialPages = useSelector(state => _.get(state, 'pages.spatial.items', []));

  const pageCount = pages.length;
  const spatialPageCount = spatialPages.length;

  let percentage = _.reduce(pages, (sum, page) => sum + page.percentageRead, 0);

  if (storyboardType === 'INTERACTIVE_CONTENT') {
    const total = pages.reduce((sum, page) => sum + (page.percentageRead || 0), 0);
    percentage = pageCount > 0 ? Math.round(total / pageCount) : 0;
  }

  if (storyboardType === 'SPATIAL') {
    if (isSmartGating) {
      const total = spatialPages.reduce(
        (sum, page) => sum + (_.get(analytics[page.canonical], 'percentageRead', 0)),
        0
      );
      percentage = spatialPageCount > 0 ? Math.round(total / spatialPageCount) : 0;
    } else {
      percentage = _.get(analytics, 'percentageRead', 0);
    }
  }
  return percentage;
}

/**
 * Check if the element is visible
*/
export function useOnScreen(ref) {
  const [isOnScreen, setIsOnScreen] = React.useState(false);
  const observerRef = React.useRef(null);

  React.useEffect(() => {
    observerRef.current = new IntersectionObserver(([entry]) =>
      setIsOnScreen(entry.isIntersecting)
    );
  }, []);

  React.useEffect(() => {
    if(ref.current) {
      observerRef.current.observe(ref.current);
    }

    return () => {
      observerRef.current.disconnect();
    };
  }, [ref]);

  return isOnScreen;
}

